/**
 * Tradução das colunas da grade entre o modelo novo e o formato do componente
 * antigo — nos DOIS sentidos, no mesmo arquivo, de propósito.
 *
 * ---
 *
 * **O defeito que este arquivo conserta.** `nodeToGridProps` repassava
 * `props.columns` como estavam, e os nomes não batem: o modelo novo guarda
 * `headerKey`, `query` e `mappings`; o `data-grid` lê `header`,
 * `searchProperties` e `fieldMappings`. O resultado no ar era uma grade com o
 * **cabeçalho em branco** em toda coluna, e coluna de busca sem entidade — sem
 * nenhum erro no console, porque ler propriedade que não existe devolve
 * `undefined` e o template imprime vazio.
 *
 * É a classe de defeito que a ponte inteira existe para conter: dois formatos
 * para a mesma coisa, e a conversão espalhada por quem precisar. Aqui ela é uma
 * função, testada, com o par inverso ao lado — porque o gerenciador de colunas
 * do inspector fala o formato antigo, e as duas direções precisam concordar. Se
 * uma mudar sozinha, o teste de ida e volta quebra.
 *
 * Some na Fase 6, quando o `data-grid` mudar de pasta e passar a ler o modelo
 * novo direto.
 */
import { GridColumn, SelectOption } from '../../model/node-props';
import { EntityQuery, FieldMapping } from '../../model/binding';

/** Como o `data-grid` antigo espera cada coluna. */
export interface LegacyGridColumn {
  field: string;
  header: string;
  type: string;
  dataPath: string;
  width?: string;
  sortable?: boolean;
  filterable?: boolean;
  editable?: boolean;
  formatter?: string;
  searchProperties?: {
    entityName: string;
    fields: { keyField: string; valueField: string };
  };
  showKey?: boolean;
  fieldMappings?: readonly LegacyFieldMapping[];
  options?: readonly LegacyOption[];
  multiple?: boolean;
  showSearch?: boolean;
}

interface LegacyOption {
  value: unknown;
  label: string;
  isDefault?: boolean;
}

/** Como o modulo antigo chama o mapeamento de campo de uma coluna de busca. */
interface LegacyFieldMapping {
  sourceColumn?: string;
  targetDataPath: string;
  targetLabel?: string;
}

/**
 * Como traduzir um rótulo.
 *
 * Entra como argumento em vez de o arquivo injetar o serviço: assim a função
 * continua pura e testável sem TestBed, e quem chama — que já tem DI — decide
 * como traduzir. `headerKey` é "chave de tradução OU texto literal", e o pipe
 * do projeto devolve a própria chave quando não acha, então passar texto
 * literal por aqui é seguro.
 */
export type TranslateFn = (key: string) => string;

/** Sem tradutor, o rótulo passa como está. Serve ao editor e aos testes. */
const AS_IS: TranslateFn = key => key;

export function toLegacyColumns(
  columns: readonly GridColumn[] | undefined,
  translate: TranslateFn = AS_IS
): readonly LegacyGridColumn[] {

  if (columns === undefined) {
    return [];
  }

  const converted: LegacyGridColumn[] = [];

  for (const column of columns) {
    converted.push(toLegacyColumn(column, translate));
  }

  return converted;
}

function toLegacyColumn(column: GridColumn, translate: TranslateFn): LegacyGridColumn {
  const legacy: LegacyGridColumn = {
    field: column.field,
    header: translate(column.headerKey ?? ''),
    type: column.type,
    dataPath: column.dataPath
  };

  copyIfSet(legacy, 'width', column.width);
  copyIfSet(legacy, 'sortable', column.sortable);
  copyIfSet(legacy, 'filterable', column.filterable);
  copyIfSet(legacy, 'editable', column.editable);
  copyIfSet(legacy, 'formatter', column.formatter);
  copyIfSet(legacy, 'showKey', column.showKey);
  copyIfSet(legacy, 'multiple', column.multiple);
  copyIfSet(legacy, 'showSearch', column.showSearch);

  if (column.query !== undefined) {
    legacy.searchProperties = {
      entityName: column.query.entityName,
      fields: {
        keyField: column.query.keyField,
        valueField: column.query.valueField
      }
    };
  }

  if (column.mappings !== undefined) {
    legacy.fieldMappings = column.mappings.map(mapping => {
      const converted: LegacyFieldMapping = { targetDataPath: mapping.targetPath };
      copyIfSet(converted, 'sourceColumn', mapping.sourceColumn);
      copyIfSet(converted, 'targetLabel', mapping.targetLabel);
      return converted;
    });
  }

  if (column.options !== undefined) {
    legacy.options = column.options.map(option => {
      const converted: LegacyOption = {
        value: option.value,
        label: translate(option.labelKey ?? '')
      };
      copyIfSet(converted, 'isDefault', option.isDefault);
      return converted;
    });
  }

  return legacy;
}

/**
 * A volta: o que o gerenciador de colunas devolveu, no modelo novo.
 *
 * O rótulo volta como TEXTO LITERAL, e é o certo: o usuário digitou aquilo na
 * tela de configuração. Se ele tivesse escolhido uma chave de tradução, teria
 * digitado a chave — e `headerKey` aceita as duas coisas.
 */
export function fromLegacyColumns(
  columns: readonly LegacyGridColumn[] | undefined
): readonly GridColumn[] {

  if (columns === undefined) {
    return [];
  }

  const converted: GridColumn[] = [];

  for (const column of columns) {
    converted.push(fromLegacyColumn(column));
  }

  return converted;
}

function fromLegacyColumn(column: LegacyGridColumn): GridColumn {
  const rebuilt: Record<string, unknown> = {
    field: column.field ?? '',
    headerKey: column.header ?? '',
    type: column.type ?? 'text',
    dataPath: column.dataPath ?? column.field ?? ''
  };

  copyIfSet(rebuilt, 'width', column.width);
  copyIfSet(rebuilt, 'sortable', column.sortable);
  copyIfSet(rebuilt, 'filterable', column.filterable);
  copyIfSet(rebuilt, 'editable', column.editable);
  copyIfSet(rebuilt, 'formatter', column.formatter);
  copyIfSet(rebuilt, 'showKey', column.showKey);
  copyIfSet(rebuilt, 'multiple', column.multiple);
  copyIfSet(rebuilt, 'showSearch', column.showSearch);

  if (column.searchProperties !== undefined) {
    const query: EntityQuery = {
      entityName: column.searchProperties.entityName,
      keyField: column.searchProperties.fields?.keyField ?? '',
      valueField: column.searchProperties.fields?.valueField ?? ''
    };
    rebuilt['query'] = query;
  }

  if (column.fieldMappings !== undefined) {
    const mappings = column.fieldMappings.map(mapping => {
      const converted: FieldMapping = { targetPath: mapping.targetDataPath };
      copyIfSet(converted, 'sourceColumn', mapping.sourceColumn);
      copyIfSet(converted, 'targetLabel', mapping.targetLabel);
      return converted;
    });
    rebuilt['mappings'] = mappings;
  }

  if (column.options !== undefined) {
    const options = column.options.map(option => {
      const converted: SelectOption = {
        value: option.value as string | number | boolean,
        labelKey: option.label ?? ''
      };
      copyIfSet(converted, 'isDefault', option.isDefault);
      return converted;
    });
    rebuilt['options'] = options;
  }

  return rebuilt as unknown as GridColumn;
}

/**
 * Copia só o que foi definido.
 *
 * Gravar `sortable: undefined` não é o mesmo que não gravar: o `undefined`
 * aparece no JSON serializado como chave ausente, mas em comparações e em
 * `Object.keys` ele existe — e uma coluna "igual" passa a divergir da outra.
 */
function copyIfSet(target: object, key: string, value: unknown): void {
  if (value === undefined) {
    return;
  }

  // O cast e local e existe porque uma interface nomeada nao e atribuivel a
  // `Record<string, unknown>` — ela nao tem assinatura de indice. Trocar as
  // interfaces por `Record` custaria a checagem de nome de campo, que e
  // justamente o que este arquivo existe para nao errar.
  (target as Record<string, unknown>)[key] = value;
}
